import {
  Checkbox,
  Column,
  EmptyState,
  EmptyStateSizeEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormInputSizeEnum,
  IconSizeEnum,
  LoaderIcon,
  Select,
  SpecialClassNames,
  Table,
  TableOptions,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { SortDirectionDropdown } from '@/components/SortDirectionDropdown/SortDirectionDropdown';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { TableRow } from '@/components/TableRow';
import { CLIP_REVIEW_STATES, SELECTED_ROW_OUTLINE_CLASSES, STALE_ROWS_CLASSES } from '@/constants';
import { useWorkspaces } from '@/features/app/api/get-workspaces';
import { TWorkspaceEntity } from '@/features/app/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useCutsActiveWorkspaces } from '@/features/broadcast-schedule-program/hooks/use-cuts-active-workspaces';
import { useGetClipsCategories } from '@/features/clips/api/get-clip-categories';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { EClipState, TClip } from '@/features/clips/types';
import { withSelectedOptions } from '@/features/mentions/components/MentionFilters/active-options/active-options';
import { useOptionsStatusMessage } from '@/features/mentions/components/MentionFilters/active-options/OptionsFooter';
import { TReviewStateValues } from '@/features/segments/types';
import { formatDateCustom } from '@/helpers/format-date';
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel';
import { SortOrder } from '@/types';

import { BroadcastScheduleProgramBodySegmentToolkitCutsManagementBar } from './BroadcastScheduleProgramBodySegmentToolkitCutsManagementBar';
import { useBroadcastScheduleClipsFilters } from './store/broadcast-schedule-clips-filters';

type TBroadcastScheduleTableProps = {
  items: TClip[];
  totalItems: number;
  isLoadingClipsBySegment: boolean;
  selectedClipId?: string | null;
  onPreview?: (data?: { url: string; body: string } | null | undefined) => void;
  onUpdate?: (item: TClip) => void;
  onDelete?: (item: TClip) => void;
  onRedirect?: (item: TClip) => void;
  onRowClick?: (item: TClip) => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitCutTable({
  items,
  totalItems,
  isLoadingClipsBySegment,
  selectedClipId: selectedClipIdProp,
  onPreview,
  onUpdate,
  onDelete,
  onRedirect,
  onRowClick,
}: TBroadcastScheduleTableProps) {
  const { segmentItems, clipsPaging, toolkitCardRegionRef } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { data: clipCategories = [] } = useGetClipsCategories();
  const { data: workspaces = [] } = useWorkspaces();
  const { paginationValues, filters, setSearchQuery, setSortBy, setFilters } =
    useBroadcastScheduleClipsFilters();

  const [selectedItems, setSelectedItems] = useState<TClip[]>([]);
  const selectedClipId = selectedClipIdProp ?? null;
  const isMounted = useRef(false);

  const { mutate: patchClipMutate, isPending } = usePatchClip({
    invalidateClipQueries: true,
    config: {
      onSuccess: () => setSelectedItems([]),
    },
  });

  const handleBatchUpdateStatus = (reviewState: TReviewStateValues) => {
    selectedItems.forEach((clip, index) => {
      const isLast = index === selectedItems.length - 1;
      patchClipMutate({ id: clip.id, review_state: reviewState, silently: !isLast });
    });
  };

  // Keyed on the identity of the query behind the list, never on `items`: with scroll loading
  // `items` changes on every appended batch, and resetting there would drop the reviewer's
  // selection mid-scroll. `listKey` changes exactly when the loaded list is discarded, so
  // selecting another segment in the programme view — which restarts nothing there — keeps it.
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setSelectedItems([]);
  }, [clipsPaging?.listKey]);

  const handleRowClick = useCallback(
    (item: TClip) => {
      onRowClick?.(item);
    },
    [onRowClick]
  );

  const getSpecialClassNames = useCallback(
    (item: TClip): SpecialClassNames => ({
      row: clsx({ [SELECTED_ROW_OUTLINE_CLASSES]: selectedClipId === item.id }),
    }),
    [selectedClipId]
  );

  const toggleItem = (clip: TClip) => {
    setSelectedItems(prev =>
      prev.some(s => s.id === clip.id) ? prev.filter(s => s.id !== clip.id) : [...prev, clip]
    );
  };

  const toggleAll = () => {
    const allSelected = items.every(item => selectedItems.some(s => s.id === item.id));
    setSelectedItems(allSelected ? [] : items);
  };

  const selectedWorkspaces = (filters.workspaces as string[] | undefined) ?? [];
  const activeWorkspaces = useCutsActiveWorkspaces();

  // Only the active workspaces the cuts mention are offered, as in the filter bar above.
  // RISK: the control derives its chips by matching the selection against these options, and the
  // dropdown footer carrying `Limpiar` hangs off those chips. A ticked workspace missing from the
  // list would therefore narrow the cuts with nothing on screen saying so and no way to undo it —
  // and `useWorkspaces` runs under `retry: false`, so one failed request is enough to empty it.
  // Carrying every selected id as an option keeps the selection visible and removable.
  const { mentionedOptions, workspacesOptions } = useMemo(() => {
    const mentioned = new Set(activeWorkspaces.ids ?? []);
    const toOption = ({ id, name }: TWorkspaceEntity) => ({ label: name, value: id });
    const active = workspaces
      .filter(({ id, state }) => state === 'active' && mentioned.has(id))
      .map(toOption);

    return {
      mentionedOptions: active,
      workspacesOptions: withSelectedOptions(
        active,
        selectedWorkspaces,
        workspaces.map(toOption),
        t('(no disponible)')
      ),
    };
  }, [workspaces, activeWorkspaces.ids, selectedWorkspaces, t]);

  const statusMessage = useOptionsStatusMessage({
    isFetching: activeWorkspaces.isFetching,
    isError: activeWorkspaces.isError,
    isEmpty: mentionedOptions.length === 0,
  });
  // Loading is announced only until the first answer: a refetch after a clip mutation keeps the
  // options it already listed and says nothing over them.
  const isFirstLoad = activeWorkspaces.ids === undefined && !activeWorkspaces.isError;
  const workspacesMessage = isFirstLoad ? t('Cargando...') : statusMessage;

  // RISK: the control only shows `emptyState` when it has no option at all, and the selection is
  // always among the options. A message for the list beside a ticked workspace — loading, failed,
  // nothing mentioned — therefore goes in as the heading of the one group the options form, the
  // only text the multiple-choice menu lays out above them.
  const workspacesGroups = workspacesMessage
    ? [{ label: workspacesMessage, options: workspacesOptions }]
    : undefined;

  // RISK: `Table` calls a filter's `onChange` during render, as a pure transform over the rows
  // it holds — not when the reviewer picks a value. Writing the chosen workspace to the store
  // from there re-enters the render and loops forever, so the filter cannot be declared through
  // `options.filters`; it is rendered here and reports on a real change event instead.
  // RISK: `withFilter` has no default in the multiple-choice variant — the `options.length > 6`
  // rule belongs to the simple variant's hook and to the table's declared-filters path, neither
  // of which reaches here — so leaving it out drops the option search over ~180 workspaces.
  const workspaceFilter = (
    <Select<string>
      multiple={true}
      size={FormInputSizeEnum.ExtraSmall}
      placeholder={t('Espacios de trabajo')}
      value={selectedWorkspaces}
      options={workspacesOptions}
      groupedOptions={workspacesGroups}
      onOpen={activeWorkspaces.onOpen}
      onMenuClose={activeWorkspaces.onClose}
      // RISK: also what the option search shows when nothing matches, so it must never be blank.
      emptyState={
        <Typography fontSize={FontSizeEnum.Sm}>
          {workspacesMessage ?? t('Sin resultados')}
        </Typography>
      }
      withFilter={true}
      showSelectAll={false}
      // Sorted so an equivalent selection always hashes to the same list query key: the key is
      // built from the filters, so `['a','b']` and `['b','a']` would restart the list for nothing.
      onChange={values => setFilters({ workspaces: [...values].sort() })}
    />
  );

  // `Table` also resolves search and sorting client-side over the rows it is handed. Both are
  // neutralised so they stay resolved where they are today: the search box only reports to the
  // store (no column declares `searchable`, so the client-side pass returns the rows untouched)
  // and the sort direction comes from the toolbar dropdown.
  // `ignoreEmpty` keeps the toolbar rendered when there are no cuts, but in `Table` it also
  // suppresses the design system's own empty state — unlike `TablePaginated`, whose inner branch
  // keys on `isFetching` instead. So the column headers are dropped when there is nothing to head
  // and the message is rendered below by hand.
  const hasNoCuts = items.length === 0 && !isLoadingClipsBySegment;
  const isRestarting = clipsPaging?.isRestarting ?? false;

  const options: Partial<TableOptions> = {
    pagination: false,
    sortable: false,
    columns: !hasNoCuts,
    onSearchChange: setSearchQuery,
    filters: [],
    searchBarPlaceholder: t('Buscar por descripción'),
  };

  const allSelected =
    items.length > 0 && items.every(item => selectedItems.some(s => s.id === item.id));
  const someSelected = selectedItems.length > 0 && !allSelected;

  const columns: Column<TClip>[] = useMemo(
    () => [
      {
        id: 'check',
        title: <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />,
        width: '3%',
        render: item => (
          <Checkbox
            checked={selectedItems.some(s => s.id === item.id)}
            onChange={() => toggleItem(item)}
          />
        ),
      },
      {
        id: 'description',
        title: t('Descripción'),
        onColumnClick: handleRowClick,
        render: item => (
          <Tooltip grow help={item.body}>
            <TableRow content={item.body || ''} className="line-clamp-1 !font-semibold" />
          </Tooltip>
        ),
      },
      {
        id: 'start_datetime',
        title: t('Inicio'),
        onColumnClick: handleRowClick,
        render: item => {
          return (
            <TableRow
              content={formatDateCustom({
                input: item.start_datetime,
                format: 'dd/MM/yyyy HH:mm:ss',
              })}
            />
          );
        },
      },
      {
        id: 'end_datetime',
        title: t('Fin'),
        onColumnClick: handleRowClick,
        render: item => {
          return (
            <TableRow
              content={formatDateCustom({
                input: item.end_datetime,
                format: 'dd/MM/yyyy HH:mm:ss',
              })}
            />
          );
        },
      },
      {
        id: 'seccion',
        title: t('Temáticas'),
        onColumnClick: handleRowClick,
        render: item => {
          const category = clipCategories.find(c => c.id === item.clip_category_id);
          return <TableRow content={category?.name || ''} />;
        },
      },
      {
        id: 'fiable',
        title: t('Fiabilidad'),
        onColumnClick: handleRowClick,
        render: () => <></>,
      },
      {
        id: 'state',
        title: t('Estado'),
        onColumnClick: handleRowClick,
        render: item => (
          <StatusSeenChipInTable
            classname="w-fit"
            review_state={item.review_state}
            reviewStates={CLIP_REVIEW_STATES}
          ></StatusSeenChipInTable>
        ),
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onPreview={() => onPreview?.({ url: item.presigned_url || '', body: item.body })}
            onSegments={() => onRedirect?.(item)}
            onUpdate={() => onUpdate?.(item)}
            onDelete={() => onDelete?.(item)}
            disabledFields={{
              preview: item.state !== EClipState.GENERATED,
            }}
          />
        ),
      },
    ],
    [
      t,
      clipCategories,
      segmentItems,
      onPreview,
      onUpdate,
      onDelete,
      selectedItems,
      allSelected,
      someSelected,
      handleRowClick,
    ]
  );

  const hasSelection = selectedItems.length > 0;

  const sortDirection = paginationValues.sortBy?.order ?? 'asc';

  const handleSortDirectionChange = (direction: string) => {
    setSortBy({
      id: paginationValues.sortBy?.id ?? 'start_datetime',
      order: direction as SortOrder,
    });
  };

  const sentinelRef = useInfiniteScrollSentinel({
    rootRef: toolkitCardRegionRef,
    hasNextPage: clipsPaging?.hasNextPage ?? false,
    isFetchingNextPage: clipsPaging?.isFetchingNextPage ?? false,
    fetchNextPage: clipsPaging?.fetchNextPage ?? (() => {}),
    itemCount: items.length,
  });

  // `Table` derives its own counter from the rows it holds, which under scroll loading means
  // "loaded so far". The server-side total is stated next to it through `afterResult`, the
  // only slot the pre-header lays out in the same row as that counter.
  const totalCutsLabel = (
    <Typography fontSize={FontSizeEnum.Sm}>
      {t('de {{total}} cortes', { total: totalItems })}
    </Typography>
  );

  const sortDirectionDropdownComponent = (
    <div data-id="filter-cuts-bar" className="flex flex-row items-center gap-2 pb-1">
      <SortDirectionDropdown
        value={sortDirection}
        onChange={handleSortDirectionChange}
        optionsClassName="!absolute !left-2"
      />
    </div>
  );

  return (
    <div>
      {hasSelection && (
        <BroadcastScheduleProgramBodySegmentToolkitCutsManagementBar
          selectedItems={selectedItems}
          onClearSelection={() => setSelectedItems([])}
          onBatchUpdateStatus={handleBatchUpdateStatus}
          isLoading={isPending}
        />
      )}

      <div className={clsx(!hasSelection && 'mt-6')} aria-busy={isRestarting}>
        <Table
          // Lands on the `table` element, so the stale rows dim while the toolbar the reviewer
          // is typing into stays as it is.
          className={clsx(isRestarting && STALE_ROWS_CLASSES)}
          renderCustom
          customFilter={workspaceFilter}
          customExtraBar={sortDirectionDropdownComponent}
          afterResult={totalCutsLabel}
          options={options}
          columns={columns}
          data={items}
          ignoreWrap
          ignoreEmpty
          isLoading={isLoadingClipsBySegment}
          getSpecialClassNames={getSpecialClassNames}
        />

        {hasNoCuts && (
          <EmptyState
            size={EmptyStateSizeEnum.MD}
            title={t('Búsqueda sin resultados')}
            subtitle={t('Prueba con otros criterios de filtrado')}
          />
        )}

        {clipsPaging?.hasNextPage && <div ref={sentinelRef} className="h-10" />}

        {clipsPaging?.isFetchingNextPage && (
          <div
            role="status"
            aria-label={t('Cargando más cortes')}
            className="my-4 flex w-full items-center justify-center"
          >
            <Typography weight={FontWeightEnum.Semibold} fontSize={FontSizeEnum.Sm}>
              <LoaderIcon size={IconSizeEnum['2XS']} />
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
